import React, { useState, useEffect } from "react";
import Carousel from "react-bootstrap/Carousel";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import "bootstrap/dist/css/bootstrap.min.css";
import OverlayContainer from "./OverlayContainer";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { imgUrl } from "../config/api";

const styles = {
  carouselContainer: {
    position: "relative",
    overflow: "hidden",
  },

  productImageContainer: {
    width: "100%",
    position: "relative",
    marginBottom: "15px",
  },

  productsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
  },
  productsListItem: {
    display: "inline-block",
    flex: "0 1 calc(32.6% - 10px)",
  },
  "@media (max-width: 923px)": {
    productsListItem: {
      width: "calc(50% - 10px)", // برای دو ستون در صفحه کوچکتر از 924px
    },
  },
};

/**
 * @import {Product} from "../JsDoc/types.js"
 *
/**
 * @param {Product} product
 * @param {string[]} colorParams
 * @returns {boolean}
 */
const filterProductByColor = (product, colorParams) => {
  if (colorParams.length > 0) {
    const colorParents = product.ColorParents.map((color) => color.ColorParentEnName.toLowerCase());
    return colorParams.some((color) => colorParents.includes(color));
  } else {
    return true;
  }
};

function ProductCarousel({ filteredProducts, selectedSort, colorParams, listRef }) {
  const [productPhotos, setProductPhotos] = useState({});
  const [activeProducts, setActiveProducts] = useState({});
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [pageLanguage, setPageLanguage] = useState(
    location.pathname
      .split("")
      .slice(1, 3)
      .join("")
  );

  const formatNumber = (number) => {
    return number.toLocaleString(i18n.language === "fa" ? "fa-IR" : "en-US");
  };
  const { catID } = useParams();
  const handleGroupProductClick = (groupProduct, currentProductId) => {
    const photos = groupProduct.Photos.filter(
      (photo) => photo.PhotoTypeId !== 4708 && photo.PhotoTypeId !== 4709
    );

    setProductPhotos((prevPhotos) => ({
      ...prevPhotos,
      [currentProductId]: photos,
    }));
    setActiveProducts((prev) => ({
      ...prev,
      [currentProductId]: groupProduct,
    }));
  };

  const groupedProducts = filteredProducts?.reduce((acc, product) => {
    if (!acc[(product?.DesignCode)]) {
      acc[(product?.DesignCode)] = [];
    }
    acc[(product?.DesignCode)].push(product);
    return acc;
  }, {});

  let priceSummary = {};

  filteredProducts?.forEach((product) => {
    let DesignCode = product?.DesignCode;

    if (!priceSummary[DesignCode]) {
      priceSummary[DesignCode] = {
        minPrice: product?.Price,
        maxPrice: product?.Price,
        minDiscountedPrice: product?.DiscountedPrice,
        maxDiscountedPrice: product?.DiscountedPrice,
      };
    } else {
      if (product.Price < priceSummary[DesignCode].minPrice) {
        priceSummary[DesignCode].minPrice = product.Price;
      }
      if (product.Price > priceSummary[DesignCode].maxPrice) {
        priceSummary[DesignCode].maxPrice = product.Price;
      }

      if (product.DiscountedPrice < priceSummary[DesignCode].minDiscountedPrice) {
        priceSummary[DesignCode].minDiscountedPrice = product.DiscountedPrice;
      }
      if (product.DiscountedPrice > priceSummary[DesignCode].maxDiscountedPrice) {
        priceSummary[DesignCode].maxDiscountedPrice = product.DiscountedPrice;
      }
    }
  });

  const displayedProducts = new Set();

  filteredProducts.sort((a, b) => {
    // گرفتن خلاصه قیمت‌ها برای هر محصول
    const aPriceSummary = priceSummary[a.DesignCode] || {};
    const bPriceSummary = priceSummary[b.DesignCode] || {};

    const aMinPrice = aPriceSummary?.minPrice || 0;
    const bMinPrice = bPriceSummary?.minPrice || 0;

    if (selectedSort === "Price, High to Low") {
    } else if (selectedSort === "Price, Low to High") {
      return aMinPrice - bMinPrice;
    } else if (selectedSort === "New") {
      const result = new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime();
      return result;
    }
  });

  useEffect(() => {
    if (
      pageLanguage !==
      location.pathname
        .split("")
        .slice(1, 3)
        .join("")
    ) {
      const tempLang = location.pathname.split("");
      setPageLanguage(tempLang.slice(1, 3).join(""));
    }
  }, [location.pathname]);

  return (
    <>
      {filteredProducts?.length > 0 ? (
        <ul
          ref={listRef}
          className="products_list products_list-new"
          style={{ gap: "2%", rowGap: "5rem" }}
        >
          {(filteredProducts ?? [])
            .filter((p) => filterProductByColor(p, colorParams))
            .map((product, index) => {
              const { DesignCode, ProductGroupId, ProductENName, ColorId } = product;
              let sameGroupProducts = groupedProducts[DesignCode] || [];
              let uniqueByColorId = sameGroupProducts.reduce((acc, current) => {
                if (!acc.some((item) => item.ColorId === current.ColorId)) {
                  acc.push(current);
                }
                return acc;
              }, []);
              const productPriceSummary = priceSummary[DesignCode] || {};
              const minPrice = productPriceSummary.minPrice || "N/A";
              const maxPrice = productPriceSummary.maxPrice || "N/A";
              const minDiscountedPrice = productPriceSummary.minDiscountedPrice || "N/A";
              const maxDiscountedPrice = productPriceSummary.maxDiscountedPrice || "N/A";
              const activeProductVariant = activeProducts[product.ProductId] || product;
              const isSoldOut =
                Number(activeProductVariant?.StockQty) === 0

              const productIdentifier = `${ProductENName}-${ColorId}`;
              if (!displayedProducts.has(productIdentifier)) {
                displayedProducts.add(productIdentifier);

                const uniqueColors = new Map();
                return (
                  <li
                    key={"list" + index}
                    className={`products_list_item products_collection_item products_list_item-three`}
                    style={
                      window.innerWidth < 924
                        ? styles["@media (max-width: 923px)"].productsListItem
                        : styles.productsListItem
                    }
                  >
                    <div
                      className="carousel-container"
                      // @ts-ignore
                      style={styles.carouselContainer}
                      onMouseEnter={(e) => {
                        // @ts-ignore
                        e.currentTarget.querySelector(".carousel-control-prev").style.opacity = "1";
                        // @ts-ignore
                        e.currentTarget.querySelector(".carousel-control-next").style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        // @ts-ignore
                        e.currentTarget.querySelector(".carousel-control-prev").style.opacity = "0";
                        // @ts-ignore
                        e.currentTarget.querySelector(".carousel-control-next").style.opacity = "0";
                      }}
                    >
                      <Carousel
                        interval={null}
                        prevIcon={<BsChevronLeft style={{ color: "black", fontSize: "3rem" }} />}
                        nextIcon={<BsChevronRight style={{ color: "black", fontSize: "3rem" }} />}
                        data-bs-theme="dark"
                        // @ts-ignore
                        style={styles.productImageContainer}
                      >
                        {(productPhotos[product.ProductId] || product.Photos)
                          .filter(
                            (photo) =>
                              photo.PhotoTypeId !== 4705 &&
                              photo.PhotoTypeId !== 4708 &&
                              photo.PhotoTypeId !== 4709
                          )
                          .map((photo) => (
                            <Carousel.Item key={photo.PhotoOrder}>
                              <div className="products_list_item_img">
                                <div className="unselectable pointer active carousel-item">
                                  <Link
                                    className="products_list_item_link 2"
                                    to={{
                                      pathname:
                                        "/" +
                                        pageLanguage +
                                        "/" +
                                        location.pathname.split("/")[2] +
                                        "/" +
                                        catID +
                                        "/" +
                                        ProductGroupId +
                                        "/" +
                                        DesignCode,
                                      search: `Product=${product.ProductId}`,
                                    }}
                                  >
                                    <img
                                      className="img-fluid"
                                      src={imgUrl(photo.PhotoUrl)}
                                      alt={product.ProductName}
                                      style={{ width: "100%", height: "auto" }}
                                    />
                                  </Link>
                                </div>
                              </div>
                            </Carousel.Item>
                          ))}
                      </Carousel>
                    </div>
                    {
                      <div className="products_list_item_body">
                        <div
                          className="Accessories_List_colorPhoto"
                          style={{ columnGap: ".75rem" }}
                        >
                          <div className="sameGroupProducts_collection">
                            {sameGroupProducts
                              .filter((product) => {
                                if (!uniqueColors.has(product.ColorId)) {
                                  uniqueColors.set(product.ColorId, product);
                                  return true;
                                } else {
                                  return false;
                                }
                              })
                              .slice(0, 4)
                              .map((groupProduct, idx) => {
                                const groupColorPhotoPath =
                                  groupProduct.Photos.find((photo) => photo.PhotoTypeId === 4709)
                                    ?.PhotoUrl || "";
                                return (
                                  <OverlayContainer
                                    key={idx}
                                    classNames="Accessories_List_item_color"
                                    placement="middle"
                                    children={
                                      <div
                                        className="Accessories_List_item_colors_list_item_overlay"
                                        onClick={() =>
                                          handleGroupProductClick(groupProduct, product.ProductId)
                                        }
                                        
                                        style={{
                                          backgroundImage: `url(${imgUrl(groupColorPhotoPath)})`,
                                          backgroundSize: "cover",
                                        }}
                                      />
                                    }
                                    component={
                                      <div className="Accessories_List_item_color_container">
                                        <p className="Accessories_List_item_color_text">
                                          {i18n.language === "en"
                                            ? groupProduct.ColorEnName
                                            : groupProduct.ColorName}
                                        </p>
                                      </div>
                                    }
                                  />
                                );
                              })}

                            {uniqueByColorId.length > 4 && (
                              <div
                                className="Accessories_List_item_color moreItem"
                                onClick={() => {
                                  console.log("More colors clicked");
                                }}
                              >
                                <p className="Accessories_List_More">{t("+ More")}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div
                          className="products_list_item_body_desc title_product"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: "1rem",
                            gap: ".75rem",
                            fontSize: "0.875rem",
                            fontWeight: "400",
                          }}
                        >
                          <Link
                            className="products_list_item_link 3"
                            to={{
                              pathname:
                                "/" +
                                pageLanguage +
                                "/" +
                                location.pathname.split("/")[2] +
                                "/" +
                                catID +
                                "/" +
                                ProductGroupId +
                                "/" +
                                DesignCode,
                              search: `Product=${product.ProductId}`,
                            }}
                          >
                            <div className="products_list_item_body_desc_title title_product">
                              <span>
                                {i18n.language === "en" ? product.DesignEnName : product.DesignName}
                                <span style={{ padding: "0 0.2rem" }}>
                                  {ProductGroupId === "10" && t("Fabric By The Meter")}
                                </span>
                              </span>
                            </div>
                          </Link>

                          {isSoldOut ? (
                            <span className="products_list_item_body_desc_price_range sold_out_price">
                              {t("Sold Out")}
                            </span>
                          ) : minPrice === minDiscountedPrice && maxPrice === maxDiscountedPrice ? (
                            <span className="products_list_item_body_desc_price_range">
                              {minPrice === maxPrice
                                ? `${formatNumber(Math.trunc(maxPrice / 10))} ${t("TOMANS")}`
                                : i18n.language === "fa"
                                ? `${formatNumber(Math.trunc(maxPrice / 10))} - ${formatNumber(
                                    Math.trunc(minPrice / 10)
                                  )} ${t("TOMANS")}`
                                : `${formatNumber(Math.trunc(minPrice / 10))} - ${formatNumber(
                                    Math.trunc(maxPrice / 10)
                                  )} ${t("TOMANS")}`}
                            </span>
                          ) : (
                            <>
                              <span style={{ textDecoration: "line-through" }}>
                                {minPrice === maxPrice
                                  ? `${formatNumber(Math.trunc(maxPrice / 10))} ${t("TOMANS")}`
                                  : i18n.language === "fa"
                                  ? `${formatNumber(Math.trunc(maxPrice / 10))} - ${formatNumber(
                                      minPrice / 10
                                    )} ${t("TOMANS")}`
                                  : `${formatNumber(Math.trunc(minPrice / 10))} - ${formatNumber(
                                      Math.trunc(maxPrice / 10)
                                    )} ${t("TOMANS")}`}
                              </span>

                              <div className="products_list_item_body_desc_more_desc">
                                {minDiscountedPrice === maxDiscountedPrice
                                  ? `${formatNumber(Math.trunc(maxDiscountedPrice / 10))} ${t(
                                      "TOMANS"
                                    )}`
                                  : i18n.language === "fa"
                                  ? `${formatNumber(
                                      Math.trunc(maxDiscountedPrice / 10)
                                    )} - ${formatNumber(Math.trunc(minDiscountedPrice / 10))} ${t(
                                      "TOMANS"
                                    )}`
                                  : `${formatNumber(
                                      Math.trunc(minDiscountedPrice / 10)
                                    )} - ${formatNumber(Math.trunc(maxDiscountedPrice / 10))} ${t(
                                      "TOMANS"
                                    )}`}
                              </div>
                            </>
                          )}
                          {!isSoldOut && (
                            <div className="products_list_item_body_desc_more_desc">
                              {i18n.language === "en"
                                ? activeProductVariant.DiscountEnName
                                : activeProductVariant.DiscountName}
                            </div>
                          )}
                        </div>
                      </div>
                    }
                  </li>
                );
              } else {
                return null;
              }
            })}
        </ul>
      ) : (
        <p></p>
      )}
    </>
  );
}

export default ProductCarousel;
